import React, { FC, useContext, useMemo, useState } from "react";
import { useApi } from "../api/APIContext";
import { useRealtimeConnection, useRealtimeMessages } from "@traas/lib/src/realtime/RealtimeConnectionContext";
import { Employee } from "@traas/lib/src/api/dto/Employee";

export type OnlineEmployee = Employee & { selectedDate?: Date };
export interface OnlineUser {
  sessionId: string;
  sub: string;
  email: string | null;
  name: string;
  selectedDate?: Date;
}
interface OnlineEmployeesContextType {
  onlineEmployees: OnlineEmployee[];
}
export const OnlineEmployeesContext = React.createContext<OnlineEmployeesContextType>({} as OnlineEmployeesContextType);

export const OnlineEmployeesContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const { employees } = useApi();
  const { sessionId } = useRealtimeConnection();

  useRealtimeMessages((json) => {
    if (json.type === "online_users") {
      setOnlineUsers(json.users);
    }
    if (json.type === "broadcast:date_selected") {
      const { data, session } = json.data as { data: { date: string }; session: string };
      if (session !== sessionId) {
        setOnlineUsers((old) => {
          return old.map((u) => {
            if (u.sessionId !== session) {
              return u;
            }
            return {
              ...u,
              selectedDate: new Date(data.date),
            };
          });
        });
      }
    }
  });

  const onlineEmployees = useMemo(() => {
    return (
      onlineUsers
        ?.filter((u) => u.sessionId !== sessionId)
        .map((u) => {
          const e = employees?.find((e) => e.id === u.sub);
          if (!e) return null;
          return {
            ...e,
            selectedDate: u.selectedDate,
          };
        })
        .filter((u) => !!u) ?? []
    );
  }, [onlineUsers, employees, sessionId]);

  return <OnlineEmployeesContext.Provider value={{ onlineEmployees }}>{children}</OnlineEmployeesContext.Provider>;
};

export const useOnlineEmployees = () => {
  return useContext(OnlineEmployeesContext);
};
